import { ActionIcon, Badge, Button, Group, Paper, Stack, Text } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconPlus } from "@tabler/icons-react";
import type { JSX } from "react";
import type { TtsProjectQuery } from "./queries";
import classes from "./TtsProjectView.module.css";

type BlockType = NonNullable<TtsProjectQuery["ttsProject"]>["blocks"]["edges"][number]["node"];

interface IProps {
    blocks: BlockType[];
    selectedBlockId: number | null;
    onSelect: (blockId: number) => void;
    onMove: (blockId: number, direction: -1 | 1) => void;
    onAdd: () => void;
    isProcessing: boolean;
    isMoveFetching: boolean;
}

function BlockList({
    blocks,
    selectedBlockId,
    onSelect,
    onMove,
    onAdd,
    isProcessing,
    isMoveFetching,
}: IProps): JSX.Element {
    return (
        <Paper withBorder shadow="xs" p="md" radius="md" className={classes.listPanel}>
            <Group justify="space-between" mb="sm">
                <Text fw={600}>Blocks</Text>
                <Button leftSection={<IconPlus size={16} />} onClick={onAdd} variant="light" size="xs">
                    Add Block
                </Button>
            </Group>
            <Stack gap="xs" className={classes.blockList}>
                {blocks.map((block) => (
                    <Paper
                        key={block.id}
                        withBorder
                        p="sm"
                        radius="md"
                        className={`${classes.blockListItem} ${
                            selectedBlockId === block.id ? classes.blockListItemActive : ""
                        }`}
                        onClick={() => onSelect(block.id)}
                    >
                        <Group justify="space-between" align="center" mb="xs">
                            <Badge size="sm" color={block.audioStale || !block.file ? "yellow" : "green"}>
                                {block.audioStale || !block.file ? "Needs regen" : "Ready"}
                            </Badge>
                            <Group gap="xs">
                                <ActionIcon
                                    variant="light"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMove(block.id, -1);
                                    }}
                                    disabled={block.position === 0 || isMoveFetching || Boolean(isProcessing)}
                                    title="Move up"
                                >
                                    <IconArrowUp size={14} />
                                </ActionIcon>
                                <ActionIcon
                                    variant="light"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMove(block.id, 1);
                                    }}
                                    disabled={
                                        block.position === blocks.length - 1 || isMoveFetching || Boolean(isProcessing)
                                    }
                                    title="Move down"
                                >
                                    <IconArrowDown size={14} />
                                </ActionIcon>
                            </Group>
                        </Group>
                        <Text size="sm" lh={1.6} lineClamp={3} className={classes.blockText}>
                            {block.text}
                        </Text>
                    </Paper>
                ))}
                {blocks.length === 0 ? (
                    <Paper withBorder p="md" radius="md" className={classes.emptyState}>
                        <Text c="dimmed">No blocks yet. Add one to start editing.</Text>
                    </Paper>
                ) : null}
            </Stack>
        </Paper>
    );
}

export default BlockList;
